import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { FilterService } from '@app/core/services/container/filter.service';
import { DivisionContextService } from '@app/core/services/division/division-context.service';
import { UserService } from '@app/core/services/user/user.service';
import { CustomFilter, FilterOutput } from '@app/shared/models/filter.interface';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { User } from '@app/shared/models/user.interface';
import { CustomOverlayService } from '@app/shared/services/overlay/custom-overlay.service';
import { EntitySelectionModel } from '@components/data/data-table-new/entitySelectionModel';
import { DataTableState } from '@components/data/data-table-new/state/state.model';
import { StateService } from '@components/data/data-table-new/state/state.service';
import { combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, startWith, tap } from 'rxjs/operators';
import { UserListOverlayTableData } from './user-list-overlay.component.table';

@Component({
  selector: 'vfs-user-list-overlay',
  templateUrl: './user-list-overlay.component.html',
  styleUrls: ['./user-list-overlay.component.scss']
})
export class UserListOverlayComponent implements OnInit {
  @Input() customFilters: Array<CustomFilter> = [];
  @Input() selected: User[];
  @Input() customDrawer: MatDrawer;
  @Input() disableInitialSelection = false;
  @Input() multiple = true;
  @Input() required = false;
  @Output() selectedChange = new EventEmitter<User[]>();
  @Output() canceled = new EventEmitter();

  all$: Observable<any>;
  selection: EntitySelectionModel<User>;
  initialSelection: User[];
  filterSubject$: Subject<FilterOutput[]>;

  constructor(
    @Inject('StateService') protected stateService: StateService,
    protected divisionContextService: DivisionContextService,
    public userListOverlayTableData: UserListOverlayTableData,
    protected userService: UserService,
    protected filterService: FilterService,
    public customOverlayService: CustomOverlayService
  ) {}

  ngOnInit() {
    this.selection = new EntitySelectionModel<User>(this.multiple, []);
    this.customOverlayService.hold(this.customDrawer);

    this.assertCorrectSelectionModelConfig();

    if (this.selected) {
      this.initialSelection = this.disableInitialSelection ? [...this.selected] : [];
      this.selection.select(...this.selected);
    }

    this.filterSubject$ = new Subject();

    const tableState$ = this.userListOverlayTableData.stateSubject$.pipe(startWith(this.getInitialState()));

    this.all$ = combineLatest([tableState$, this.divisionContextService.divisionFilter$.asObservable()]).pipe(
      tap(result => this.refreshData(result[0]))
    );
  }

  protected assertCorrectSelectionModelConfig() {
    if (!this.multiple && this.userListOverlayTableData.displayedColumns.find(column => column === 'select')) {
      const selectColumnIdx = this.userListOverlayTableData.displayedColumns.indexOf('select');
      this.userListOverlayTableData.displayedColumns.splice(selectColumnIdx, 1);
    }
  }

  protected getInitialState(): DataTableState {
    const exists = this.stateService.exists(this.userListOverlayTableData.stateId);
    const state = this.stateService.get(this.userListOverlayTableData.stateId);
    if (!exists) {
      state.sort.column = 'surname';
      state.sort.direction = 'asc';

      this.customFilters.forEach(
        customFilter => (state.filter.value[customFilter.fieldName] = customFilter.buildFun())
      );

      this.stateService.update(state);
    }
    return state;
  }

  protected refreshData(state: DataTableState) {
    this.userListOverlayTableData.datasource.loading = true;
    let filters: string = this.filterService.getStateFilters(state.filter);
    filters += this.getMandatoryFilters(filters);

    return this.userService
      .search(state.page, state.sort, state.filter.search, filters)
      .pipe(
        catchError(error => throwError(error)),
        tap(page => {
          this.userListOverlayTableData.datasource.setPageData(page);
          this.userListOverlayTableData.datasource.loading = false;
        })
      )
      .subscribe();
  }

  protected getMandatoryFilters(filters: string) {
    return (
      (filters.length > 0 ? ' and ' : '') +
      ` (* ${RSQLOperator.VISIBILITY} *)` +
      ` and (* ${RSQLOperator.EXCLUDE_B2B_USER} *)`
    );
  }

  masterToggle() {
    if (this.isPageFullSelected()) {
      this.selection.clear();
      this.selection.select(...this.initialSelection);
    } else {
      this.selection.select(...this.userListOverlayTableData.datasource.data);
    }
  }

  isPageFullSelected(): boolean {
    return this.userListOverlayTableData.datasource.data.every(user => this.selection.isSelected(user));
  }

  isPagePartiallySelected(): boolean {
    const selectedCount = this.userListOverlayTableData.datasource.data.filter(user => this.selection.isSelected(user))
      .length;
    return selectedCount > 0 && selectedCount < this.userListOverlayTableData.datasource.data.length;
  }

  public getDivisions(user: User): string {
    return user.userDivisions.map(userDivision => userDivision.division.description).join(', ');
  }

  accept() {
    this.selectedChange.emit(this.selection.selected);
  }

  disableRow(userId: string): boolean {
    return (
      this.disableInitialSelection && this.initialSelection && this.initialSelection.some(user => user.id === userId)
    );
  }

  disableAllRows(): boolean {
    return (
      !this.multiple ||
      (this.disableInitialSelection &&
        this.userListOverlayTableData.datasource.data
          .map(user => this.disableRow(user.id))
          .reduce((acc, current) => acc && current))
    );
  }

  applyFilters(filters: FilterOutput[]) {
    this.filterSubject$.next(filters);
  }
}
